import { produce } from 'immer';
import { CHAT_BUBBLE_ANIMATION_SET } from '../../animations/animationDefinitions';
import { isConfigFlagEnabled } from '../../config';
import { TURN_BET_ROUND_NUMBER } from '../../constants/constants';
import { cardStringParser } from '../../types/cardTypes';
import { ALL_MEDAL_PROPERTIES } from '../../types/medalProperties';
import { isDefaultShowdown } from '../../types/pokerTypes';
import { ALL_POKER_VARIANT_PROPERTIES } from '../../types/pokerVariantTypes';
import { maybeOverrideAvatar } from '../../utils/avatarUtils';
import { isVariantPotLimit } from '../../utils/pokerVariantUtils';
import { ADD_SEATED_PLAYER_CHIPS, BECOME_OWNER, BET_FROM_INBOUND, CLEAR_POKER_SCENE, CLEAR_WIN_CHIPS_ANIMATION, ENTER_POKER_PLAYER_FROM_INBOUND, HAND_RECONNECT_FROM_INBOUND, HAND_WIN_FROM_INBOUND, HIDE_CHAT_BUBBLE, HIDE_DEALING_MESSAGE, HIDE_POKER_ROOM_PRIZE_WIN, INIT_POKER_ROOM_FROM_INBOUND, LEAVE_POKER_PLAYER_FROM_INBOUND, RENAME_POKER_ROOM, SET_BET_SLIDER_AMOUNT, SET_BET_SLIDER_X, SET_BLIND_TIMER_FROM_INBOUND, SET_DISCARD_CARD_INDEX, SET_DISCARD_PHASE_DONE_FROM_INBOUND, SET_DISCARD_PHASE_FROM_INBOUND, SET_HAND_REVEALING_FROM_INBOUND, SET_HOLE_CARDS_AFTER_DISCARD_FROM_INBOUND, SET_IS_AUTO_CHECKING, SET_IS_AUTO_FOLDING_ANY, SET_MY_PLAYER_ID, SET_PENDING_DISCARD_INDEX, SET_PLAYER_HAS_DISCARDED_FROM_INBOUND, SET_POKER_PLAYER_BANK, SET_POKER_ROOM_INFO_FROM_INBOUND, SET_REQUESTED_BUY_IN, SET_SEATED_PLAYER_AVATAR_FROM_INBOUND, SET_SIDE_BET_PHASE_FROM_INBOUND, SET_SIT_OUT_SELF_STATUS_FROM_INBOUND, SET_SIT_OUT_STATUS_FROM_INBOUND, SET_STATUS_MESSAGE, SHOW_ALL_IN_COMMUNITY_CARDS_FROM_INBOUND, SHOW_ALL_IN_HANDS_FROM_INBOUND, SHOW_BLANK_CHAT_BUBBLE_FROM_INBOUND, SHOW_POKER_ROOM_PRIZE_WIN_FROM_INBOUND, SIT_PLAYER_FROM_INBOUND, STAND_UP_DELAYED_FROM_INBOUND, STAND_UP_FROM_INBOUND, START_HAND_FROM_INBOUND, START_SEAT_TIMERS_FROM_INBOUND, START_SHOWDOWN, START_WIN_CHIPS_ANIMATION, UNSET_PENDING_SIT_PLAYER_ANIMATION, UPDATE_CHIP_STACKS_FROM_INBOUND, UPDATE_HAND_RANK_FROM_INBOUND, UPDATE_LAST_CHIP_STACK_FROM_INBOUND, UPDATE_PLAYERS_FROM_INBOUND, UPDATE_PLAYERS_MEDALS_AND_STYLE_FROM_INBOUND, UPDATE_POKER_ROOM_FOR_CLOSED_MTT, UPDATE_POT_AMOUNTS_FROM_INBOUND, WAIT_PHASE_FROM_INBOUND } from '../actions/pokerActions';
export const DEFAULT_REMEMBER_REQUESTED_BUY_IN = true;
const initialState = {
    turnState: 'waiting',
    seatIndexesAvailable: [],
    players: [],
    seatedPlayers: {},
    seatedPlayerSeatIndexes: [],
    communityCards: [],
    rabbits: [],
    betPotActions: {},
    winChipsAnimations: {},
    potAmounts: [],
    revealedHands: {},
    minimumBet: 0,
    inboundShowdowns: [],
    isSelfSittingOut: false,
    isSelfFolded: false,
    rememberRequestedBuyIn: DEFAULT_REMEMBER_REQUESTED_BUY_IN,
    chatBubbles: {}
};
export const selectShouldShowStartTourneyButton = (state) => {
    const tourneyProperties = state.poker.tourneyProperties;
    return !!(tourneyProperties &&
        !tourneyProperties.isMtt &&
        !tourneyProperties.hasStarted &&
        tourneyProperties.isTourneyOwner);
};
/**
 * Returns true if you ("my player") is active in the hand.
 */
export const selectIsMyPlayerIn = (state) => {
    if (!state.poker.holeCards) {
        return false;
    }
    if (state.poker.mySeatIndex === undefined) {
        return false;
    }
    const myPlayer = state.poker.seatedPlayers[state.poker.mySeatIndex];
    return myPlayer ? myPlayer.isIn : false;
};
export const selectHasHoleCards = (state) => !!state.poker.holeCards?.length;
export const selectIsSimpleBettingEnabled = (state) => {
    if (!isConfigFlagEnabled('simpleBetting')) {
        return false;
    }
    const isTourney = !!state.poker.tourneyProperties;
    if (isTourney) {
        // always use "full" betting controls in tourneys
        return false;
    }
    // determine if pot limit:
    if (!state.poker.pokerVariant) {
        return false;
    }
    return !isVariantPotLimit(state.poker.pokerVariant);
};
export const selectTotalPotSize = (state) => state.poker.potAmounts.reduce((acc, curr) => acc + curr);
/**
 * Selects the time properties (start and duration of player's turn) if it is the provided seat's turn.
 */
export function selectActionSeatTimeProperties(state, seatIndex) {
    const actionTimer = state.poker.actionTimer;
    if (!actionTimer) {
        return;
    }
    const isActionSeat = actionTimer.seatIndexes.includes(seatIndex);
    if (!isActionSeat) {
        return;
    }
    const { startTimestampMs, totalMs } = actionTimer;
    return { startTimestampMs, totalMs };
}
export function getSeatedPlayerFromPlayerId(state, playerId) {
    return Object.values(state.poker.seatedPlayers).find((currSeatedPlayer) => currSeatedPlayer?.id === playerId);
}
function getPlayerCardEmphasis(seatIndex, holeCardIndex, showdown) {
    if (isDefaultShowdown(showdown)) {
        return 'normal';
    }
    const winningCardsObjForPlayer = showdown.winningCards.find((currWinningCardsObj) => currWinningCardsObj.seatIndex === seatIndex);
    if (!winningCardsObjForPlayer) {
        // player isn't winning, card is automatically dimmed
        return 'dimmed';
    }
    const holeCardIndexInRanking = winningCardsObjForPlayer.handCardIndexes.indexOf(holeCardIndex);
    if (holeCardIndexInRanking === -1) {
        // this card isn't played at all
        return 'dimmed';
    }
    if (holeCardIndexInRanking < winningCardsObjForPlayer.numRankedCards) {
        // this card is within the main ranking of the hand (not a kicker)
        return 'highlighted';
    }
    return 'normal';
}
function getCommunityCardEmphasis(communityCardIndex, showdown, pokerVariant, isAfterDiscardPhase) {
    if (!pokerVariant) {
        console.error('poker variant should already be set before community card emphasis can be determined');
        return 'normal';
    }
    // When deciding which community cards to highlight, we only care about the winning cards info for one of the
    // winning players, since their played cards should be identical:
    const winningCardsObj = showdown.winningCards[0];
    if (winningCardsObj.numRankedCards === 0) {
        // If the winning player has no ranked cards, this is a 'default' showdown:
        // TODO: Memoize this (call it 'isDefaultShowdown'?) flag prior to this check.
        return 'normal';
    }
    const communityCardIndexAfterHoleCards = communityCardIndex + ALL_POKER_VARIANT_PROPERTIES[pokerVariant].getNumHoleCards(isAfterDiscardPhase);
    const communityCardIndexInRanking = winningCardsObj.handCardIndexes.indexOf(communityCardIndexAfterHoleCards);
    if (communityCardIndexInRanking === -1) {
        return 'dimmed';
    }
    else if (communityCardIndexInRanking < winningCardsObj.numRankedCards) {
        return 'highlighted';
    }
    else {
        return 'normal';
    }
}
function getAvailableBetActions(betTotalAmountToCall, myTotalBetAmount, myNumChips, myNumChipsThisPhase, isAllInGame) {
    if (isAllInGame === true) {
        return ['fold', 'all-in'];
    }
    const availableBetActions = [];
    const isFacingABet = betTotalAmountToCall > myTotalBetAmount;
    const additionalChipsToCall = betTotalAmountToCall - myTotalBetAmount;
    availableBetActions.push(isFacingABet ? 'fold' : 'check'); // can always fold or check
    if (isFacingABet) {
        availableBetActions.push('call');
    }
    if (isFacingABet) {
        if (additionalChipsToCall < myNumChips &&
            (!isConfigFlagEnabled('onlyAllowCallOnInsufficientRaises') || additionalChipsToCall >= myNumChipsThisPhase)) {
            // can only raise if you have more than the call
            availableBetActions.push('raise');
        }
    }
    else {
        // not facing a bet
        availableBetActions.push('bet');
    }
    return availableBetActions;
}
function hasCheckOrRaiseOption(betTotalAmountToCall, myTotalBetAmount, myNumChipsThisPhase) {
    return betTotalAmountToCall === myTotalBetAmount && myNumChipsThisPhase > 0;
}
function isPotLimitThisBetRound(pokerVariant, currBetRoundNumber, isDualLimit) {
    if (isVariantPotLimit(pokerVariant)) {
        // this variant is a pot-limit-only variant, so it always plays pot limit
        return true;
    }
    if (isDualLimit &&
        ALL_POKER_VARIANT_PROPERTIES[pokerVariant].availableLimitTypes.includes('dual-limit') &&
        currBetRoundNumber < TURN_BET_ROUND_NUMBER) {
        return true;
    }
    return false;
}
function getPotLimitBetSliderMaxAmount(potAmounts, betPotActions, additionalChipsToCall, myNumChips) {
    const totalPotAmount = potAmounts.reduce((acc, amount) => acc + amount, 0);
    const betPotAmounts = Object.values(betPotActions).map((betPotAction) => betPotAction ? betPotAction.chipsBetThisRound : 0);
    const totalBetPotAmount = betPotAmounts.reduce((acc, amount) => acc + amount, 0);
    // The additional chips have to call get added twice when determining how much you can raise
    // (you call first, resulting in a total pot amount, and then you can raise that amount again):
    const betThePotSliderMaxAmount = totalBetPotAmount + totalPotAmount + additionalChipsToCall * 2;
    return Math.min(betThePotSliderMaxAmount, myNumChips);
}
function createMyTurnProperties(pokerVariant, currBetRoundNumber, betSequenceNumber, betTotalAmountToCall, minimumBet, betIncrement, myTotalBetAmount, myNumChips, myNumChipsThisPhase, potAmounts, betPotActions, isAllInGame, isDualLimit) {
    const additionalChipsToCall = betTotalAmountToCall - myTotalBetAmount;
    const availableBetActions = getAvailableBetActions(betTotalAmountToCall, myTotalBetAmount, myNumChips, myNumChipsThisPhase, isAllInGame);
    const betSliderMaxAmount = isPotLimitThisBetRound(pokerVariant, currBetRoundNumber, isDualLimit)
        ? getPotLimitBetSliderMaxAmount(potAmounts, betPotActions, additionalChipsToCall, myNumChips)
        : myNumChips;
    const betSliderMinAmount = Math.min(betSliderMaxAmount, additionalChipsToCall + minimumBet);
    function getBetOrRaiseProperties() {
        if (availableBetActions.includes('bet')) {
            const labelType = hasCheckOrRaiseOption(betTotalAmountToCall, myTotalBetAmount, myNumChipsThisPhase)
                ? 'raise'
                : 'bet';
            return { labelType, outboundType: 'bet' };
        }
        if (availableBetActions.includes('raise')) {
            return { labelType: 'raise', outboundType: 'raise' };
        }
    }
    const betOrRaiseProperties = getBetOrRaiseProperties();
    return {
        startTimestampMs: performance.now(),
        betSequenceNumber,
        canBet: availableBetActions.includes('bet'),
        canCall: availableBetActions.includes('call'),
        canCheck: availableBetActions.includes('check'),
        canFold: availableBetActions.includes('fold'),
        canRaise: availableBetActions.includes('raise'),
        canGoAllIn: availableBetActions.includes('all-in'),
        betSliderMinAmount,
        betSliderMaxAmount,
        hasRangeOfPossibleBets: betSliderMaxAmount > betSliderMinAmount,
        betIncrement,
        betTotalAmountToCall,
        myTotalBetAmount,
        myNumChips,
        myNumChipsThisPhase,
        betSliderAmount: betSliderMinAmount,
        betSliderX: 0,
        betOrRaiseLabelType: betOrRaiseProperties?.labelType,
        betOrRaiseOutboundType: betOrRaiseProperties?.outboundType
    };
}
function didOtherPlayerReopenBetting(lastAction, mySeatIndex) {
    if (!lastAction) {
        return false;
    }
    const reopeningBetActions = ['bet', 'raise', 'all-in'];
    return reopeningBetActions.includes(lastAction.actionType) && lastAction.seatIndex !== mySeatIndex;
}
function didIMakeLastAction(lastAction, mySeatIndex) {
    if (!lastAction) {
        return false;
    }
    return lastAction.seatIndex === mySeatIndex;
}
function createAutoBetProperties(betTotalAmountToCall, myTotalBetAmount, existingAutoBetProperties, lastAction, mySeatIndex) {
    const isFacingABet = betTotalAmountToCall > myTotalBetAmount;
    if (existingAutoBetProperties) {
        let { availableAutoBetActions, isAutoChecking, isAutoFoldingAny } = existingAutoBetProperties;
        if (didOtherPlayerReopenBetting(lastAction, mySeatIndex)) {
            // someone else reopened the betting
            availableAutoBetActions = [];
            if (isFacingABet) {
                // facing a bet, always have the option to auto fold in this case
                availableAutoBetActions.push('fold');
            }
        }
        else if (didIMakeLastAction(lastAction, mySeatIndex)) {
            availableAutoBetActions = [];
            isAutoChecking = false;
            isAutoFoldingAny = false;
        }
        return {
            availableAutoBetActions,
            betTotalAmountToCall,
            myTotalBetAmount,
            isAutoChecking,
            isAutoFoldingAny
        };
    }
    else {
        const availableAutoBetActions = [];
        if (isFacingABet) {
            // facing a bet, always have the option to auto fold in this case
            availableAutoBetActions.push('fold');
        }
        else if (betTotalAmountToCall === myTotalBetAmount) {
            availableAutoBetActions.push('check');
        }
        return {
            availableAutoBetActions,
            betTotalAmountToCall,
            myTotalBetAmount,
            isAutoChecking: false,
            isAutoFoldingAny: false
        };
    }
}
function addDraftSeatedPlayerSeatIndex(draft, seatIndex) {
    if (!draft.seatedPlayerSeatIndexes.includes(seatIndex)) {
        draft.seatedPlayerSeatIndexes.push(seatIndex);
        draft.seatedPlayerSeatIndexes.sort();
    }
}
function removeDraftSeatedPlayerSeatIndex(draft, seatIndex) {
    const index = draft.seatedPlayerSeatIndexes.findIndex((currSeatIndex) => currSeatIndex === seatIndex);
    if (index !== -1) {
        draft.seatedPlayerSeatIndexes.splice(index, 1);
    }
}
function maybeSetDraftLastAction(draft, lastAction, betSequenceNumber, isNewBetRound) {
    if (!lastAction) {
        return;
    }
    draft.betPotActions[lastAction.seatIndex] = {
        actionType: lastAction.actionType,
        chipsBetThisRound: isNewBetRound ? 0 : lastAction.chipsBetThisRound,
        betSequenceNumber: betSequenceNumber
    };
    if (lastAction.actionType === 'fold') {
        const seatedPlayer = draft.seatedPlayers[lastAction.seatIndex];
        if (seatedPlayer) {
            seatedPlayer.isIn = false;
            if (seatedPlayer.id === draft.myPlayerId) {
                draft.isSelfFolded = true;
                delete draft.autoBetProperties;
            }
        }
    }
}
function maybeCreateActiveBlindTimer(roundSecondsTotal, roundSecondsRemaining) {
    if (roundSecondsTotal) {
        // blind timer is only "active" with non-zero total seconds
        return {
            roundSecondsTotal: roundSecondsTotal,
            startTimestampSeconds: Math.floor(Date.now() / 1000) - (roundSecondsTotal - roundSecondsRemaining)
        };
    }
}
function createPokerPlayerFromInbound(inboundPokerPlayer) {
    const { id, name, numChipsInBank, isPowerPlayer } = inboundPokerPlayer;
    return { id, name, numChipsInBank, isPowerPlayer };
}
/**
 * Create an array of medals that will be shown on the seated player.
 * Only shows the Power Player medal if they have no other medals.
 * (The presence of any medals at all should indicate that they are at least a Power Player.)
 */
function createVisibleMedals(medals) {
    if (!medals.length) {
        return [];
    }
    const visibleMedals = medals
        .map((medal) => {
        const imageUrl = ALL_MEDAL_PROPERTIES[medal.type].getImageUrl?.(medal);
        return imageUrl ? { medalType: medal.type, name: medal.name, imageUrl } : undefined;
    })
        .filter((medal) => !!medal);
    // If there is more than one visible medal, only show the non-power-player ones.
    // (Power Player medal should always be one of the medals if player is a Power Player,
    // but becomes a bit redundant and doesn't need to be shown if other medals exist.)
    if (visibleMedals.length > 1) {
        return visibleMedals.filter(({ medalType }) => medalType !== 'pp');
    }
    return visibleMedals;
}
function createSeatedPlayerFromInbound(inboundPokerPlayer) {
    const { id, name, seatIndex, numChips, avatar, isSittingOut, isIn, medals, isPowerPlayer } = inboundPokerPlayer;
    if (seatIndex !== undefined) {
        return {
            id,
            name,
            seatIndex,
            numChips,
            avatar: maybeOverrideAvatar(id, name, avatar) || avatar,
            isSittingOut,
            isIn,
            medals,
            visibleMedals: createVisibleMedals(medals),
            isStandingUp: false, // default seated players to 'not standing up' since we don't have that info in inbounds (at room enter time, etc)
            isSitAnimationPending: false,
            isPowerPlayer
        };
    }
}
function maybeSetDraftIsSelfFoldedAtShowdown(draft, currentShowdown) {
    if (draft.mySeatIndex === undefined) {
        return;
    }
    if (currentShowdown.potIndex > 0) {
        // after the first pot, players who were implicitly folded at the start of the showdown will have already been updated
        return;
    }
    const myPlayerShowdownInfo = currentShowdown.playerShowdownInfos.find((playerShowdownInfo) => playerShowdownInfo.playerId === draft.myPlayerId);
    // Players whose folding triggered the showdown will be listed as a showdown player but won't be showing cards.
    // In this case, the player must have folded:
    if (myPlayerShowdownInfo && !myPlayerShowdownInfo.isShowingCards) {
        draft.isSelfFolded = true;
    }
}
function draftClearExpiredChatBubbleAnimations(draft, nowMs) {
    nowMs = nowMs || performance.now();
    // add a short "grace period" to ensure the animation finishes rendering:
    const expiredAnimationTimestampMs = nowMs - CHAT_BUBBLE_ANIMATION_SET.totalDurationMs - 500;
    Object.entries(draft.chatBubbles).forEach(([seatIndex, chatBubble]) => {
        if (chatBubble.startTimestampMs < expiredAnimationTimestampMs) {
            delete draft.chatBubbles[seatIndex];
        }
    });
}
const pokerReducer = (state = initialState, action) => {
    return produce(state, (draft) => {
        switch (action.type) {
            case CLEAR_POKER_SCENE: {
                return initialState;
            }
            case INIT_POKER_ROOM_FROM_INBOUND: {
                draft.roomName = action.payload.roomName;
                draft.buyIn = action.payload.buyIn;
                draft.hasBuyInRange = action.payload.hasBuyInRange;
                draft.minBuyIn = action.payload.minBuyIn;
                draft.maxBuyIn = action.payload.maxBuyIn;
                draft.smallBlind = action.payload.smallBlind;
                draft.bigBlind = action.payload.bigBlind;
                draft.currency = action.payload.currency;
                draft.turnState = action.payload.turnState;
                draft.communityCards = action.payload.communityCards;
                draft.seatIndexesAvailable = action.payload.seatIndexesAvailable;
                draft.players = action.payload.inboundPokerPlayers.map(createPokerPlayerFromInbound);
                action.payload.inboundPokerPlayers.forEach((inboundPokerPlayer) => {
                    const newSeatedPlayer = createSeatedPlayerFromInbound(inboundPokerPlayer);
                    if (newSeatedPlayer) {
                        draft.seatedPlayers[newSeatedPlayer.seatIndex] = newSeatedPlayer;
                        addDraftSeatedPlayerSeatIndex(draft, newSeatedPlayer.seatIndex);
                    }
                });
                draft.pokerVariant = action.payload.pokerVariant;
                draft.statusMessage = action.payload.statusMessage;
                draft.timerDelayTotalMs = action.payload.timerDelayTotalMs;
                draft.isSelfSittingOut = action.payload.isSittingOut;
                // determine if I am already sitting:
                const mySeatedPlayer = Object.values(draft.seatedPlayers).find((seatedPlayer) => seatedPlayer && seatedPlayer.id === draft.myPlayerId);
                if (mySeatedPlayer) {
                    draft.mySeatIndex = mySeatedPlayer.seatIndex;
                }
                if (action.payload.isTourneyTable) {
                    draft.tourneyProperties = {
                        isMtt: action.payload.isMtt,
                        hasStarted: action.payload.hasTourneyStarted,
                        isTourneyOwner: action.payload.isTourneyOwner
                    };
                    const activeBlindTimer = maybeCreateActiveBlindTimer(action.payload.inboundBlindTimer.roundSecondsTotal, action.payload.inboundBlindTimer.roundSecondsRemaining);
                    if (activeBlindTimer) {
                        draft.tourneyProperties.activeBlindTimer = activeBlindTimer;
                    }
                }
                else {
                    delete draft.tourneyProperties;
                }
                draft.isDualLimit = action.payload.isDualLimit;
                draft.isMixedGame = action.payload.isMixedGame;
                draft.isAllInGame = action.payload.isAllInGame;
                // infer if we should be after the discard phase based on betting round and variant on room entry:
                const pokerVariantProperties = ALL_POKER_VARIANT_PROPERTIES[action.payload.pokerVariant];
                if (pokerVariantProperties.discardAfterBetRound &&
                    action.payload.betRoundNumber > pokerVariantProperties.discardAfterBetRound) {
                    draft.isAfterDiscardPhase = true;
                }
                draft.sideBetCost = action.payload.sideBetAmount;
                break;
            }
            case SIT_PLAYER_FROM_INBOUND: {
                const { playerId, seatIndex, numChips, isSittingOut, avatar } = action.payload;
                const player = draft.players.find((player) => player.id === playerId);
                if (player) {
                    player.numChipsInBank = action.payload.numChipsInBank;
                    if (playerId === draft.myPlayerId) {
                        draft.mySeatIndex = action.payload.seatIndex;
                        draft.isSelfSittingOut = action.payload.isSittingOut;
                    }
                    const newSeatedPlayer = {
                        id: playerId,
                        name: player.name,
                        seatIndex,
                        numChips,
                        avatar: maybeOverrideAvatar(playerId, player.name, avatar) || avatar,
                        isSittingOut,
                        isStandingUp: false,
                        isIn: false,
                        medals: [],
                        visibleMedals: [],
                        isSitAnimationPending: true, // waits for the avatar to be fully loaded before showing the sit animation
                        isPowerPlayer: player.isPowerPlayer
                    };
                    draft.seatedPlayers[seatIndex] = newSeatedPlayer;
                    addDraftSeatedPlayerSeatIndex(draft, seatIndex);
                }
                break;
            }
            case STAND_UP_FROM_INBOUND: {
                const { playerId } = action.payload;
                if (playerId === draft.myPlayerId) {
                    delete draft.mySeatIndex;
                    delete draft.autoBetProperties;
                    draft.isSelfSittingOut = false;
                    if (draft.holeCards && draft.holeCards.length) {
                        // Standing up abruptly will not normally show as a fold and won't have an associated inbound to this effect.
                        // If you stood up, show yourself as folded if you had cards:
                        draft.isSelfFolded = true;
                    }
                }
                const seatedPlayer = Object.values(draft.seatedPlayers).find((seatedPlayer) => seatedPlayer?.id === playerId);
                if (seatedPlayer) {
                    delete draft.seatedPlayers[seatedPlayer.seatIndex];
                    removeDraftSeatedPlayerSeatIndex(draft, seatedPlayer.seatIndex);
                    delete draft.chatBubbles[seatedPlayer.seatIndex];
                }
                break;
            }
            case STAND_UP_DELAYED_FROM_INBOUND: {
                const { playerId } = action.payload;
                const seatedPlayer = Object.values(draft.seatedPlayers).find((currSeatedPlayer) => currSeatedPlayer?.id === playerId);
                if (seatedPlayer) {
                    seatedPlayer.isStandingUp = true;
                }
                break;
            }
            case ENTER_POKER_PLAYER_FROM_INBOUND: {
                const { inboundPokerPlayer } = action.payload;
                const newPlayer = createPokerPlayerFromInbound(inboundPokerPlayer);
                const existingPlayerIndex = draft.players.findIndex((player) => player.id === newPlayer.id);
                if (existingPlayerIndex !== -1) {
                    // this is likely an existing player that is reconnecting to the room, replace it
                    draft.players.splice(existingPlayerIndex, 1, newPlayer);
                }
                else {
                    draft.players.push(newPlayer);
                }
                const newSeatedPlayer = createSeatedPlayerFromInbound(inboundPokerPlayer);
                if (newSeatedPlayer) {
                    // TODO: Determine if this can ever happen -- a player who enters and is already seated, maybe when reconnecting to a room?
                    draft.seatedPlayers[newSeatedPlayer.seatIndex] = newSeatedPlayer;
                    addDraftSeatedPlayerSeatIndex(draft, newSeatedPlayer.seatIndex);
                }
                break;
            }
            case LEAVE_POKER_PLAYER_FROM_INBOUND: {
                const playerId = action.payload.playerId;
                draft.players = draft.players.filter((player) => player.id !== playerId);
                const seatedPlayer = Object.values(draft.seatedPlayers).find((seatedPlayer) => seatedPlayer?.id === playerId);
                if (seatedPlayer) {
                    delete draft.seatedPlayers[seatedPlayer.seatIndex];
                    removeDraftSeatedPlayerSeatIndex(draft, seatedPlayer.seatIndex);
                }
                break;
            }
            case START_HAND_FROM_INBOUND: {
                const { holeCards, tourneyHasStarted, inSeatIndexes, pokerVariant, activeHandId, smallBlind, bigBlind } = action.payload;
                const performanceTimestampMs = performance.now();
                draft.startHandTimestampMs = performanceTimestampMs;
                draft.turnState = 'bet';
                draft.holeCards = holeCards.map((holeCard, index) => ({
                    card: holeCard,
                    index,
                    cardEmphasis: 'normal',
                    performanceTimestampMs
                }));
                draft.smallBlind = smallBlind;
                draft.bigBlind = bigBlind;
                draft.potAmounts = [];
                draft.communityCards = [];
                draft.rabbits = [];
                delete draft.winningSeatIndexes;
                draft.inboundShowdowns = [];
                delete draft.currentShowdown;
                draft.revealedHands = {};
                delete draft.statusMessage;
                delete draft.isDiscardPhase;
                delete draft.isAfterDiscardPhase;
                delete draft.discardCardIndex;
                delete draft.pendingDiscardIndex;
                if (draft.tourneyProperties) {
                    draft.tourneyProperties.hasStarted = tourneyHasStarted;
                }
                Object.values(draft.seatedPlayers).forEach((seatedPlayer) => {
                    if (seatedPlayer) {
                        seatedPlayer.isIn = inSeatIndexes.includes(seatedPlayer.seatIndex);
                    }
                });
                draft.isSelfFolded = false;
                draft.isRevealingThisHand = false;
                draft.pokerVariant = pokerVariant;
                draft.isShowingDealingMessage = true;
                delete draft.prizeWin;
                draft.activeHandId = activeHandId;
                break;
            }
            case START_SEAT_TIMERS_FROM_INBOUND: {
                draft.actionTimer = {
                    startTimestampMs: performance.now() - (action.payload.totalMs - action.payload.remainingMs),
                    seatIndexes: action.payload.seatIndexes,
                    totalMs: action.payload.totalMs
                };
                break;
            }
            case BET_FROM_INBOUND: {
                const { buttonSeatIndex, actionSeatIndex, newCommunityCards, smallBlindSeatIndex, minimumBet, betSequenceNumber, bigBlindSeatIndexes, lastAction, potAmounts, chipsBetPerPlayer, betRoundNumber, newBetRoundNumber, isMyTurn, betTotalAmountToCall, betIncrement, myTotalBetAmount } = action.payload;
                draft.buttonSeatIndex = buttonSeatIndex;
                draft.actionSeatIndex = actionSeatIndex;
                const performanceTimestampMs = performance.now();
                draft.communityCards = draft.communityCards.concat(newCommunityCards.map((newCommunityCard) => ({ ...newCommunityCard, performanceTimestampMs })));
                if (newCommunityCards.length) {
                    // new street, previous auto-betting settings are no longer relevant:
                    delete draft.autoBetProperties;
                    // don't show 'dealing' message past the flop:
                    delete draft.isShowingDealingMessage;
                }
                if (smallBlindSeatIndex !== undefined) {
                    draft.betPotActions[smallBlindSeatIndex] = {
                        actionType: 'small blind',
                        chipsBetThisRound: minimumBet / 2,
                        betSequenceNumber
                    };
                }
                if (bigBlindSeatIndexes.length) {
                    bigBlindSeatIndexes.forEach((bigBlindSeatIndex, i) => {
                        draft.betPotActions[bigBlindSeatIndex] = {
                            actionType: i === 0 ? 'big blind' : 'post',
                            chipsBetThisRound: minimumBet,
                            betSequenceNumber
                        };
                    });
                }
                if (newBetRoundNumber !== -1) {
                    draft.betPotActions = {};
                }
                maybeSetDraftLastAction(draft, lastAction, betSequenceNumber, newBetRoundNumber !== -1);
                draft.potAmounts = potAmounts;
                chipsBetPerPlayer.forEach((chipsBetPerPlayer) => {
                    const seatedPlayer = Object.values(draft.seatedPlayers).find((seatedPlayer) => seatedPlayer?.id === chipsBetPerPlayer.playerId);
                    if (seatedPlayer) {
                        seatedPlayer.numChips = chipsBetPerPlayer.numChips;
                    }
                });
                const isSelfIn = !!Object.values(draft.seatedPlayers).find((seatedPlayer) => seatedPlayer?.id === draft.myPlayerId && seatedPlayer?.isIn);
                if (isSelfIn) {
                    draft.autoBetProperties = createAutoBetProperties(betTotalAmountToCall, myTotalBetAmount, draft.autoBetProperties, lastAction, draft.mySeatIndex);
                }
                draft.minimumBet = minimumBet;
                delete draft.isMyDiscardTurn;
                delete draft.isDiscardPhase;
                if (isMyTurn) {
                    const myPlayerChipsObj = chipsBetPerPlayer.find((playerChipsObj) => draft.myPlayerId === playerChipsObj.playerId);
                    if (!myPlayerChipsObj) {
                        console.error('My number of chips not provided in payload');
                        break;
                    }
                    if (!draft.pokerVariant) {
                        console.error('Poker variant is not known when bet inbound is processed');
                        break;
                    }
                    // There are quirks with betRoundNumber and newBetRoundNumber.
                    // newBetRoundNumber will be -1 unless the bet round is changing.
                    // betRoundNumber will be the previous bet round if this bet action happens when the bet round is changing.
                    // Therefore both of these variables are needed to reliably determine the *current* bet round.
                    // TODO: Consider changing what these variables mean on the server side to remove this nuance,
                    // perhaps by making it so betRoundNumber never refers to the previous bet round.
                    const currBetRoundNumber = newBetRoundNumber !== -1 ? newBetRoundNumber : betRoundNumber;
                    draft.myTurnProperties = createMyTurnProperties(draft.pokerVariant, currBetRoundNumber, betSequenceNumber, betTotalAmountToCall, minimumBet, betIncrement, myTotalBetAmount, myPlayerChipsObj.numChips, myPlayerChipsObj.numChipsThisPhase, draft.potAmounts, draft.betPotActions, draft.isAllInGame, draft.isDualLimit);
                }
                else {
                    delete draft.myTurnProperties;
                }
                break;
            }
            case HAND_WIN_FROM_INBOUND: {
                const { inboundShowdowns } = action.payload;
                draft.inboundShowdowns = inboundShowdowns;
                draft.rabbits = inboundShowdowns[0].rabbits;
                break;
            }
            case START_SHOWDOWN: {
                const showdownIndex = action.payload;
                const inboundShowdown = state.inboundShowdowns[showdownIndex];
                if (!inboundShowdown) {
                    // An error was seen on the live server where the showdown object was not set,
                    // perhaps due to entering a room while a multi-pot showdown is in progress.
                    // In this case, the client may not have received the win: inbound containing showdown definitions.
                    break;
                }
                // now that the showdown is actually happening, store its start timestamp in redux:
                const currentShowdown = { ...inboundShowdown, startTimeStampMs: performance.now() };
                draft.currentShowdown = currentShowdown;
                draft.turnState = 'showdown';
                delete draft.actionSeatIndex;
                const winningSeatIndexes = currentShowdown.winningCards.map(({ seatIndex }) => seatIndex);
                draft.winningSeatIndexes = winningSeatIndexes;
                Object.values(draft.seatedPlayers).forEach((seatedPlayer) => {
                    if (seatedPlayer) {
                        seatedPlayer.isIn = winningSeatIndexes.includes(seatedPlayer.seatIndex);
                    }
                });
                maybeSetDraftIsSelfFoldedAtShowdown(draft, currentShowdown);
                draft.revealedHands = currentShowdown.playerShowdownInfos.reduce((acc, currPlayerShowdownInfo) => {
                    if (currPlayerShowdownInfo.isShowingCards) {
                        const { seatIndex } = currPlayerShowdownInfo;
                        acc[seatIndex] = currPlayerShowdownInfo.revealedCards.map((card, holeCardIndex) => {
                            return {
                                card,
                                cardEmphasis: getPlayerCardEmphasis(seatIndex, holeCardIndex, currentShowdown)
                            };
                        });
                    }
                    return acc;
                }, {});
                draft.communityCards = draft.communityCards.map((communityCard, i) => {
                    const cardEmphasis = getCommunityCardEmphasis(i, currentShowdown, state.pokerVariant, state.isAfterDiscardPhase);
                    return { ...communityCard, cardEmphasis };
                });
                draft.potAmounts = currentShowdown.potAmounts;
                draft.betPotActions = {};
                delete draft.myTurnProperties;
                delete draft.autoBetProperties;
                delete draft.actionTimer;
                if (draft.holeCards) {
                    draft.holeCards = draft.holeCards.map((holeCard) => ({ ...holeCard, cardEmphasis: 'dimmed' }));
                }
                delete draft.sideBetPhaseProperties;
                break;
            }
            case SHOW_ALL_IN_HANDS_FROM_INBOUND: {
                const { lastAction, allInPlayerHands } = action.payload;
                // all existing community cards need to have their card emphasis removed (paired/matched cards):
                draft.communityCards = draft.communityCards.map((communityCard) => {
                    return { ...communityCard, cardEmphasis: 'normal' };
                });
                draft.revealedHands = allInPlayerHands.reduce((acc, allInPlayerHand) => {
                    acc[allInPlayerHand.seatIndex] = allInPlayerHand.revealedCards.map((card) => ({
                        card,
                        cardEmphasis: 'normal' // showdown hasn't happened yet, so no cards are emphasized
                    }));
                    return acc;
                }, {});
                if (lastAction) {
                    draft.betPotActions[lastAction.seatIndex] = {
                        actionType: lastAction.actionType,
                        chipsBetThisRound: lastAction.chipsBetThisRound,
                        betSequenceNumber: -1
                    };
                }
                delete draft.myTurnProperties;
                delete draft.autoBetProperties;
                if (draft.holeCards) {
                    draft.holeCards = draft.holeCards.map((holeCard) => ({ ...holeCard, cardEmphasis: 'dimmed' }));
                }
                delete draft.actionTimer;
                delete draft.actionSeatIndex;
                delete draft.sideBetPhaseProperties;
                maybeSetDraftLastAction(draft, lastAction, -1);
                break;
            }
            case SHOW_ALL_IN_COMMUNITY_CARDS_FROM_INBOUND: {
                const { cardsWithIndexes, potAmounts } = action.payload;
                const performanceTimestampMs = performance.now();
                cardsWithIndexes.forEach((cardWithIndex) => {
                    draft.communityCards.push({
                        index: cardWithIndex.cardIndex,
                        card: cardWithIndex.card,
                        cardEmphasis: 'normal',
                        performanceTimestampMs
                    });
                });
                draft.potAmounts = potAmounts;
                draft.betPotActions = {};
                break;
            }
            case SET_BET_SLIDER_AMOUNT: {
                if (draft.myTurnProperties) {
                    draft.myTurnProperties.betSliderAmount = action.payload;
                }
                break;
            }
            case SET_BET_SLIDER_X: {
                if (draft.myTurnProperties) {
                    draft.myTurnProperties.betSliderX = action.payload;
                }
                break;
            }
            case WAIT_PHASE_FROM_INBOUND: {
                draft.turnState = 'waiting';
                draft.communityCards = [];
                draft.rabbits = [];
                delete draft.winningSeatIndexes;
                draft.inboundShowdowns = [];
                delete draft.currentShowdown;
                draft.revealedHands = {};
                draft.statusMessage = action.payload.message;
                draft.potAmounts = [];
                delete draft.holeCards;
                draft.isSelfFolded = false;
                delete draft.isShowingDealingMessage;
                delete draft.prizeWin;
                break;
            }
            case SET_BLIND_TIMER_FROM_INBOUND: {
                const { roundSecondsTotal, roundSecondsRemaining } = action.payload;
                const activeBlindTimer = maybeCreateActiveBlindTimer(roundSecondsTotal, roundSecondsRemaining);
                if (activeBlindTimer) {
                    // if non-zero round duration, blind timer is still active
                    if (!draft.tourneyProperties) {
                        console.error('attempted to set blind timer when no tourney properties object is set');
                        break;
                    }
                    draft.tourneyProperties.activeBlindTimer = activeBlindTimer;
                }
                else {
                    // blind timer is no longer active
                    if (draft.tourneyProperties) {
                        delete draft.tourneyProperties.activeBlindTimer;
                    }
                }
                break;
            }
            case UPDATE_PLAYERS_FROM_INBOUND: {
                action.payload.playersToUpdate.forEach((playerToUpdate) => {
                    const seatedPlayer = Object.values(draft.seatedPlayers).find((currSeatedPlayer) => currSeatedPlayer?.id === playerToUpdate.playerId);
                    if (seatedPlayer) {
                        seatedPlayer.numChips = playerToUpdate.numChips;
                    }
                });
                break;
            }
            case ADD_SEATED_PLAYER_CHIPS: {
                const { seatIndex, numChipsToAdd } = action.payload;
                const seatedPlayer = draft.seatedPlayers[seatIndex];
                if (seatedPlayer) {
                    seatedPlayer.numChips += numChipsToAdd;
                }
                break;
            }
            case SET_POKER_PLAYER_BANK: {
                const existingPlayer = draft.players.find((player) => player.id === action.payload.playerId);
                if (existingPlayer) {
                    existingPlayer.numChipsInBank = action.payload.numChipsInBank;
                }
                break;
            }
            case SET_SIT_OUT_SELF_STATUS_FROM_INBOUND: {
                // NOTE: Basically identical to SET_SIT_OUT_STATUS_FROM_INBOUND.
                // (Could be simplified by changing the API to have just one message for sit out status changes.)
                const { playerId, isSittingOut } = action.payload;
                const seatedPlayer = Object.values(draft.seatedPlayers).find((currSeatedPlayer) => currSeatedPlayer?.id === playerId);
                if (seatedPlayer) {
                    seatedPlayer.isSittingOut = isSittingOut;
                }
                draft.isSelfSittingOut = isSittingOut;
                break;
            }
            case SET_SIT_OUT_STATUS_FROM_INBOUND: {
                // NOTE: Basically identical to SET_SIT_OUT_SELF_STATUS_FROM_INBOUND.
                // (Could be simplified by changing the API to have just one message for sit out status changes.)
                const { playerId, isSittingOut } = action.payload;
                const seatedPlayer = Object.values(draft.seatedPlayers).find((currSeatedPlayer) => currSeatedPlayer?.id === playerId);
                if (seatedPlayer) {
                    seatedPlayer.isSittingOut = isSittingOut;
                }
                // if my sit out status is being updated, it should come through a sitout_self: inbound, but just in case, cover it here too:
                if (draft.myPlayerId === playerId) {
                    draft.isSelfSittingOut = isSittingOut;
                }
                break;
            }
            case SET_POKER_ROOM_INFO_FROM_INBOUND: {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { _type, ...rest } = action.payload;
                draft.roomInfo = { ...rest };
                break;
            }
            case HAND_RECONNECT_FROM_INBOUND: {
                draft.holeCards = action.payload.holeCards.map((holeCard, index) => ({
                    card: holeCard,
                    index,
                    cardEmphasis: 'normal'
                }));
                draft.isDiscardPhase = action.payload.isDiscardPhase;
                draft.isMyDiscardTurn = draft.isDiscardPhase && !!action.payload.holeCards.length;
                if (draft.isMyDiscardTurn) {
                    // On room entry during discard phase, by default a 'Discard Phase' table status will appear.
                    // Since the current player is active in the discard phase, hide the message.
                    delete draft.statusMessage;
                    draft.pendingDiscardIndex = action.payload.discardedCardIndex;
                }
                break;
            }
            case BECOME_OWNER: {
                if (draft.tourneyProperties) {
                    draft.tourneyProperties.isTourneyOwner = true;
                }
                break;
            }
            case UPDATE_HAND_RANK_FROM_INBOUND: {
                if (!draft.pokerVariant) {
                    console.error('poker variant should already be set before hand rank card emphasis can be set');
                    break;
                }
                const { cardIndexes } = action.payload;
                const { getNumHoleCards } = ALL_POKER_VARIANT_PROPERTIES[draft.pokerVariant];
                draft.communityCards = draft.communityCards.map((communityCard, i) => ({
                    ...communityCard,
                    cardEmphasis: cardIndexes.includes(i + getNumHoleCards(draft.isAfterDiscardPhase)) ? 'outlined' : 'normal'
                }));
                if (draft.holeCards) {
                    draft.holeCards = draft.holeCards.map((holeCard, i) => {
                        return { ...holeCard, cardEmphasis: cardIndexes.includes(i) ? 'outlined' : 'normal' };
                    });
                }
                break;
            }
            case SET_STATUS_MESSAGE: {
                if (action.payload) {
                    draft.statusMessage = action.payload;
                }
                else {
                    delete draft.statusMessage;
                }
                break;
            }
            case UPDATE_POKER_ROOM_FOR_CLOSED_MTT: {
                draft.communityCards = [];
                draft.rabbits = [];
                // "Stand up" all players, which is what the Flash version did, I think. TODO: verify this.
                draft.seatedPlayers = {};
                draft.seatedPlayerSeatIndexes = [];
                // stop blind timer:
                if (draft.tourneyProperties) {
                    delete draft.tourneyProperties.activeBlindTimer;
                }
                delete draft.currentShowdown;
                draft.potAmounts = [];
                break;
            }
            case SET_MY_PLAYER_ID: {
                draft.myPlayerId = action.payload;
                break;
            }
            case SET_DISCARD_PHASE_FROM_INBOUND: {
                draft.potAmounts = action.payload.potAmounts;
                delete draft.actionSeatIndex;
                draft.actionTimer = {
                    startTimestampMs: performance.now(),
                    totalMs: draft.timerDelayTotalMs || 30000, // use a default just in case it isn't set
                    seatIndexes: action.payload.discardSeatIndexesRemaining
                };
                delete draft.myTurnProperties;
                delete draft.autoBetProperties;
                if (action.payload.isMyDiscardTurn) {
                    draft.isMyDiscardTurn = true;
                }
                else {
                    delete draft.isMyDiscardTurn;
                }
                draft.isDiscardPhase = true;
                maybeSetDraftLastAction(draft, action.payload.lastAction, -1);
                break;
            }
            case UPDATE_POT_AMOUNTS_FROM_INBOUND: {
                draft.potAmounts = action.payload.potAmounts;
                break;
            }
            case SET_SEATED_PLAYER_AVATAR_FROM_INBOUND: {
                const { seatIndex, avatar } = action.payload;
                if (seatIndex >= 0) {
                    const seatedPlayer = draft.seatedPlayers[seatIndex];
                    if (seatedPlayer) {
                        seatedPlayer.avatar = maybeOverrideAvatar(seatedPlayer.id, seatedPlayer.name, avatar) || avatar;
                    }
                }
                break;
            }
            case UPDATE_PLAYERS_MEDALS_AND_STYLE_FROM_INBOUND: {
                action.payload.updatedPlayersMedalsAndStyle.forEach(({ medals, playerId }) => {
                    const seatedPlayer = Object.values(draft.seatedPlayers).find((currSeatedPlayer) => currSeatedPlayer?.id === playerId);
                    if (seatedPlayer) {
                        seatedPlayer.medals = medals;
                        seatedPlayer.visibleMedals = createVisibleMedals(medals);
                    }
                });
                break;
            }
            case SET_IS_AUTO_CHECKING: {
                if (draft.autoBetProperties) {
                    const isAutoChecking = action.payload;
                    draft.autoBetProperties.isAutoChecking = isAutoChecking;
                    if (isAutoChecking) {
                        // auto check and fold any are mutually exclusive
                        draft.autoBetProperties.isAutoFoldingAny = false;
                    }
                }
                break;
            }
            case SET_IS_AUTO_FOLDING_ANY: {
                if (draft.autoBetProperties) {
                    const isAutoFoldingAny = action.payload;
                    draft.autoBetProperties.isAutoFoldingAny = isAutoFoldingAny;
                    if (isAutoFoldingAny) {
                        // auto check and fold any are mutually exclusive
                        draft.autoBetProperties.isAutoChecking = false;
                    }
                }
                break;
            }
            case UNSET_PENDING_SIT_PLAYER_ANIMATION: {
                const seatIndex = action.payload;
                const seatedPlayer = draft.seatedPlayers[seatIndex];
                if (seatedPlayer) {
                    seatedPlayer.isSitAnimationPending = false;
                }
                break;
            }
            case SET_HAND_REVEALING_FROM_INBOUND: {
                draft.isRevealingThisHand = action.payload.isRevealingThisHand;
                break;
            }
            case SET_REQUESTED_BUY_IN: {
                if (!draft.buyIn || !draft.minBuyIn || !draft.maxBuyIn || !draft.hasBuyInRange) {
                    return;
                }
                const { requestedBuyIn, rememberRequestedBuyIn } = action.payload;
                draft.requestedBuyIn = Math.max(draft.minBuyIn, Math.min(requestedBuyIn, draft.maxBuyIn));
                if (rememberRequestedBuyIn !== undefined) {
                    draft.rememberRequestedBuyIn = rememberRequestedBuyIn;
                }
                break;
            }
            case SET_DISCARD_CARD_INDEX: {
                draft.discardCardIndex = action.payload;
                break;
            }
            case SET_HOLE_CARDS_AFTER_DISCARD_FROM_INBOUND: {
                if (draft.holeCards) {
                    draft.holeCards = draft.holeCards.filter((holeCard) => action.payload.holeCardShorthands.includes(holeCard.card.shorthand));
                }
                break;
            }
            case SET_DISCARD_PHASE_DONE_FROM_INBOUND: {
                draft.isAfterDiscardPhase = true;
                delete draft.pendingDiscardIndex;
                delete draft.isMyDiscardTurn;
                delete draft.actionTimer;
                delete draft.statusMessage;
                const { updatedAllInPlayerHands } = action.payload;
                updatedAllInPlayerHands.forEach((updatedAllInPlayerHand) => {
                    const { playerId, cardShorthands } = updatedAllInPlayerHand;
                    const seatedPlayer = Object.values(draft.seatedPlayers).find((currSeatedPlayer) => currSeatedPlayer?.id === playerId);
                    if (!seatedPlayer) {
                        return;
                    }
                    const { seatIndex } = seatedPlayer;
                    draft.revealedHands[seatIndex] = cardShorthands.map((cardShorthand) => ({
                        card: cardStringParser(cardShorthand),
                        cardEmphasis: 'normal'
                    }));
                });
                break;
            }
            case SET_PENDING_DISCARD_INDEX: {
                draft.pendingDiscardIndex = action.payload;
                break;
            }
            case SET_PLAYER_HAS_DISCARDED_FROM_INBOUND: {
                if (draft.actionTimer) {
                    draft.actionTimer = {
                        ...draft.actionTimer,
                        seatIndexes: draft.actionTimer.seatIndexes.filter((seatIndex) => seatIndex !== action.payload.seatIndex)
                    };
                }
                break;
            }
            case UPDATE_LAST_CHIP_STACK_FROM_INBOUND: {
                draft.lastChipStack = action.payload.lastChipStack;
                break;
            }
            case SHOW_BLANK_CHAT_BUBBLE_FROM_INBOUND: {
                const nowMs = performance.now();
                draft.chatBubbles[action.payload.seatIndex] = { startTimestampMs: nowMs };
                draftClearExpiredChatBubbleAnimations(draft, nowMs);
                break;
            }
            case HIDE_CHAT_BUBBLE: {
                delete draft.chatBubbles[action.payload];
                draftClearExpiredChatBubbleAnimations(draft);
                break;
            }
            case HIDE_DEALING_MESSAGE: {
                delete draft.isShowingDealingMessage;
                break;
            }
            case RENAME_POKER_ROOM: {
                draft.roomName = action.payload;
                break;
            }
            case SHOW_POKER_ROOM_PRIZE_WIN_FROM_INBOUND: {
                const { winnerId, messageToOthers, messageToWinner, prizeDescription, durationMs } = action.payload;
                draft.prizeWin = { winnerId, messageToOthers, messageToWinner, prizeDescription, durationMs };
                break;
            }
            case HIDE_POKER_ROOM_PRIZE_WIN: {
                delete draft.prizeWin;
                break;
            }
            case UPDATE_CHIP_STACKS_FROM_INBOUND: {
                const { playerChipStacks } = action.payload;
                playerChipStacks.forEach(({ seatIndex, numChips }) => {
                    const seatedPlayer = draft.seatedPlayers[seatIndex];
                    if (!seatedPlayer) {
                        return;
                    }
                    seatedPlayer.numChips = numChips;
                });
                break;
            }
            case START_WIN_CHIPS_ANIMATION: {
                if (isConfigFlagEnabled('chipsAddedAnimation')) {
                    draft.winChipsAnimations[action.payload] = performance.now();
                }
                break;
            }
            case CLEAR_WIN_CHIPS_ANIMATION: {
                if (isConfigFlagEnabled('chipsAddedAnimation')) {
                    delete draft.winChipsAnimations[action.payload];
                }
                break;
            }
            case SET_SIDE_BET_PHASE_FROM_INBOUND: {
                const { durationMs, lastAction } = action.payload;
                draft.sideBetPhaseProperties = {
                    startTimestampMs: performance.now(),
                    durationMs
                };
                maybeSetDraftLastAction(draft, lastAction, -1);
                delete draft.myTurnProperties;
                delete draft.autoBetProperties;
                delete draft.actionTimer;
                delete draft.actionSeatIndex;
                break;
            }
            default: {
                return state;
            }
        }
    });
};
export default pokerReducer;
